/**
 * <subscribe-popup> — newsletter modal shown shortly after a page load.
 *
 * Frequency is a merchant setting: every page load, once per visit, or once
 * every N days. Subscribing suppresses it permanently, and so does a failed
 * storage read — a visitor whose browser blocks storage should not be shown a
 * modal on every single page.
 *
 * The markup ships `hidden`, so if this script never runs the popup simply
 * never appears rather than sitting open with no way to close it.
 */

const SUPPRESS_FOREVER = 'subscribed';

/**
 * A deliverable-looking address: one @, a local part without spaces, quotes
 * or stray dots, and a domain of real labels ending in a letters-only TLD.
 * Stricter than the browser's own check, which accepts "name@domain".
 */
function isValidEmail(value) {
  if (!value || value.length > 254) return false;
  const at = value.lastIndexOf('@');
  if (at < 1) return false;
  const local = value.slice(0, at);
  const domain = value.slice(at + 1).toLowerCase();
  if (local.length > 64 || !/^[^\s@"(),:;<>[\]\\]+$/.test(local)) return false;
  if (local.startsWith('.') || local.endsWith('.') || local.includes('..')) return false;
  return /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/.test(domain);
}

// Domains common enough that a near miss is almost certainly a typo.
const COMMON_DOMAINS = [
  'gmail.com', 'googlemail.com', 'yahoo.com', 'ymail.com', 'hotmail.com', 'outlook.com',
  'live.com', 'msn.com', 'icloud.com', 'me.com', 'aol.com', 'mail.com', 'proton.me',
  'protonmail.com', 'yahoo.co.uk', 'hotmail.co.uk',
];

// Edit distance counting a swapped pair of letters as one edit ("gmial").
function editDistance(a, b) {
  const d = Array.from({ length: a.length + 1 }, (_, i) => [i]);
  for (let j = 1; j <= b.length; j++) d[0][j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
  }
  return d[a.length][b.length];
}

/** "name@gmial.com" → "name@gmail.com"; null when nothing looks mistyped. */
function suggestEmail(value) {
  const at = value.lastIndexOf('@');
  if (at < 1) return null;
  const domain = value.slice(at + 1).toLowerCase();
  if (!domain || COMMON_DOMAINS.includes(domain)) return null;
  let best = null;
  let bestDistance = Infinity;
  COMMON_DOMAINS.forEach((candidate) => {
    const distance = editDistance(domain, candidate);
    if (distance < bestDistance) {
      best = candidate;
      bestDistance = distance;
    }
  });
  // Short domains get less slack, so a real "mail.co" isn't "corrected".
  const limit = domain.length < 8 ? 1 : 2;
  return bestDistance <= limit ? `${value.slice(0, at)}@${best}` : null;
}

class SubscribePopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-popup-dialog]');
    if (!this.dialog) return;

    this.key = this.dataset.storageKey || 'roya-subscribe';
    this.isPrelaunch = this.dataset.prelaunch === 'true';
    this.lastFocused = null;

    this.querySelectorAll('[data-popup-close]').forEach((button) => {
      button.addEventListener('click', () => this.close({ remember: true }));
    });

    // In prelaunch mode there is no way out: no overlay-click, no Escape (see
    // onKeydown) — the popup is meant to block the site until launch.
    const overlay = this.querySelector('[data-popup-overlay]');
    if (overlay && !this.isPrelaunch) {
      overlay.addEventListener('click', () => this.close({ remember: true }));
    }

    this.addEventListener('keydown', this.onKeydown);
    this.bindEmailCheck();

    // The form posts and the page reloads, so success arrives as server-rendered
    // markup. Confirm it with a toast rather than reopening the modal — being
    // made to dismiss a dialog to acknowledge something you just did is the
    // opposite of a reward — then never show the popup again.
    if (this.querySelector('[data-popup-success]')) {
      this.remember(SUPPRESS_FOREVER);
      this.bindCopy();
      // With a discount code the thank-you *is* the reward — it has to be seen
      // and copied — so the dialog reopens on it instead of a passing toast.
      if (this.dataset.discountCode && !this.isPrelaunch) {
        this.confirm({ toast: false });
        this.open({ focus: false });
        const copy = this.querySelector('[data-popup-copy]');
        if (copy) window.requestAnimationFrame(() => copy.focus({ preventScroll: true }));
        return;
      }
      if (this.isPrelaunch) {
        // Prelaunch keeps blocking the page — swap to the thank-you note in
        // place rather than hiding the dialog.
        this.open();
        return;
      }
      this.confirm();
      return;
    }

    // In the theme editor it must be visible to be edited, and frequency rules
    // would make it flicker in and out while the merchant works.
    if (window.Shopify && window.Shopify.designMode) {
      this.open({ focus: false });
      return;
    }

    if (this.isPrelaunch) {
      // The site isn't open yet regardless of whether this visitor already
      // joined, so prelaunch always shows — with the thank-you note standing
      // in for the form on a return visit.
      if (this.read() === SUPPRESS_FOREVER) this.showThanks();
      this.open();
      return;
    }

    if (!this.shouldShow()) return;

    const delay = (parseFloat(this.dataset.delay) || 0) * 1000;
    this.timer = setTimeout(() => this.open(), delay);
  }

  /**
   * Client-side stand-in for the server-rendered success state, used when a
   * returning visitor already joined — Liquid only renders that markup on the
   * specific request right after the form posts.
   */
  showThanks() {
    const content = this.querySelector('.subscribe-popup__content');
    if (!content || content.querySelector('[data-popup-success]')) return;

    const heading = this.querySelector('.subscribe-popup__heading');
    if (heading) heading.textContent = this.dataset.successHeading || "You're on the list";

    content.querySelectorAll('[data-popup-field], [data-popup-body], [data-popup-footnote]').forEach((el) => {
      el.remove();
    });

    const message = document.createElement('p');
    message.className = 'subscribe-popup__body';
    message.setAttribute('data-popup-success', '');
    message.textContent = this.dataset.successText || "You're on the list.";
    content.appendChild(message);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    clearTimeout(this.toastTimer);
    clearTimeout(this.settleTimer);
    clearTimeout(this.copyTimer);
    this.removeEventListener('keydown', this.onKeydown);
    document.body.classList.remove('overflow-hidden');
  }

  /* ---------------- Frequency ---------------- */

  read() {
    try {
      return window.localStorage.getItem(this.key);
    } catch (error) {
      // Storage blocked (private mode, cookie settings). Treated as "already
      // seen" so the popup cannot become an every-page interruption.
      return SUPPRESS_FOREVER;
    }
  }

  remember(value) {
    try {
      window.localStorage.setItem(this.key, value);
    } catch (error) {
      /* nothing to do — see read() */
    }
  }

  shouldShow() {
    const stored = this.read();
    if (stored === SUPPRESS_FOREVER) return false;

    const frequency = this.dataset.frequency || 'always';

    if (frequency === 'session') {
      try {
        if (window.sessionStorage.getItem(this.key)) return false;
      } catch (error) {
        return false;
      }
    }

    if (frequency === 'days') {
      const days = parseFloat(this.dataset.frequencyDays) || 7;
      const last = parseInt(stored, 10);
      if (last && Date.now() - last < days * 86400000) return false;
    }

    return true;
  }

  /* ---------------- Confirmation ---------------- */

  /**
   * Post-subscribe state: no modal, a brief toast, and the page back at the top.
   * Shopify returns from the form post with the form's anchor in the URL, which
   * lands the reader at the footer with no idea whether anything happened.
   */
  confirm({ toast: showToast = true } = {}) {
    const toast = showToast ? this.querySelector('[data-popup-toast]') : null;

    this.hidden = false;
    if (showToast) this.setAttribute('confirmed', '');

    // Drop the form anchor so a reload — or a back/forward — doesn't jump again.
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';

    // Several things can move the page after this runs: the fragment jump, a
    // stray autofocus, images settling. Re-assert through the load, rather than
    // scrolling once and hoping nothing overrides it.
    const toTop = () => {
      const focused = document.activeElement;
      if (focused && focused !== document.body && !this.contains(focused)) {
        focused.blur();
      }
      window.scrollTo(0, 0);
    };

    toTop();
    window.requestAnimationFrame(toTop);
    window.addEventListener('load', toTop, { once: true });
    this.settleTimer = setTimeout(toTop, 250);

    if (!toast) return;

    const seconds = parseFloat(this.dataset.toastDuration) || 5;
    toast.addEventListener('click', () => this.dismissToast());
    this.toastTimer = setTimeout(() => this.dismissToast(), seconds * 1000);
  }

  dismissToast() {
    clearTimeout(this.toastTimer);
    this.removeAttribute('confirmed');
    setTimeout(() => {
      if (!this.hasAttribute('confirmed') && !this.hasAttribute('open')) this.hidden = true;
    }, 300);
  }

  /* ---------------- Email check ---------------- */

  bindEmailCheck() {
    const input = this.querySelector('[data-popup-email]');
    const form = input && input.form;
    const error = this.querySelector('[data-popup-email-error]');
    if (!input || !form || !error) return;

    // The script checks the address itself, with a message that matches the
    // popup; the browser's bubble would say something different, in its own style.
    form.noValidate = true;
    const suggestion = this.querySelector('[data-popup-suggestion]');
    const fix = this.querySelector('[data-popup-suggestion-fix]');
    let offered = null;

    const showError = (show) => {
      error.hidden = !show;
      error.textContent = show ? error.dataset.message : '';
      if (show) {
        input.setAttribute('aria-invalid', 'true');
        input.setAttribute('aria-describedby', error.id);
      } else {
        input.removeAttribute('aria-invalid');
        input.removeAttribute('aria-describedby');
      }
    };

    const hideSuggestion = () => {
      if (suggestion) suggestion.hidden = true;
    };

    form.addEventListener('submit', (event) => {
      const value = input.value.trim();
      input.value = value;

      if (!isValidEmail(value)) {
        event.preventDefault();
        hideSuggestion();
        showError(true);
        input.focus();
        return;
      }
      showError(false);

      // A likely typo is offered once; sending the same address again keeps it.
      const better = suggestEmail(value);
      if (better && suggestion && fix && offered !== value) {
        event.preventDefault();
        offered = value;
        fix.textContent = better;
        suggestion.hidden = false;
        fix.focus();
        return;
      }
      hideSuggestion();
    });

    input.addEventListener('input', () => {
      if (!error.hidden && isValidEmail(input.value.trim())) showError(false);
      if (offered !== null && input.value.trim() !== offered) hideSuggestion();
    });

    if (fix) {
      fix.addEventListener('click', () => {
        input.value = fix.textContent;
        offered = input.value;
        hideSuggestion();
        input.focus();
      });
    }
  }

  /* ---------------- Discount code ---------------- */

  bindCopy() {
    const button = this.querySelector('[data-popup-copy]');
    const value = this.querySelector('[data-popup-code-value]');
    const status = this.querySelector('[data-popup-copy-status]');
    if (!button || !value) return;

    const done = () => {
      button.textContent = 'Copied';
      button.classList.add('is-copied');
      if (status) status.textContent = `Code ${value.textContent.trim()} copied`;
      clearTimeout(this.copyTimer);
      this.copyTimer = setTimeout(() => {
        button.textContent = 'Copy';
        button.classList.remove('is-copied');
      }, 2400);
    };

    // Fallback without the clipboard API: select the code so it can be copied.
    const selectCode = () => {
      const range = document.createRange();
      range.selectNodeContents(value);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    };

    button.addEventListener('click', () => {
      const code = value.textContent.trim();
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(code).then(done, selectCode);
      } else {
        selectCode();
      }
    });
  }

  /* ---------------- Open / close ---------------- */

  open({ focus = true } = {}) {
    if (this.hasAttribute('open')) return;

    this.lastFocused = document.activeElement;
    this.hidden = false;
    this.setAttribute('open', '');
    document.body.classList.add('overflow-hidden');

    if (focus) {
      const target = this.querySelector('input[type="email"]') || this.dialog;
      // Focus after the entrance so the browser does not scroll to it mid-animation.
      window.requestAnimationFrame(() => target.focus({ preventScroll: true }));
    }
  }

  close({ remember = false } = {}) {
    if (!this.hasAttribute('open')) return;

    clearTimeout(this.timer);
    this.removeAttribute('open');
    document.body.classList.remove('overflow-hidden');

    if (remember) {
      const frequency = this.dataset.frequency || 'always';
      if (frequency === 'session') {
        try {
          window.sessionStorage.setItem(this.key, String(Date.now()));
        } catch (error) {
          /* ignore */
        }
      } else if (frequency === 'days') {
        this.remember(String(Date.now()));
      }
    }

    // Wait out the exit transition before going inert again.
    setTimeout(() => {
      if (!this.hasAttribute('open')) this.hidden = true;
    }, 200);

    if (this.lastFocused && document.contains(this.lastFocused)) {
      this.lastFocused.focus({ preventScroll: true });
    }
  }

  onKeydown = (event) => {
    if (!this.hasAttribute('open')) return;

    if (event.key === 'Escape') {
      if (this.isPrelaunch) return;
      event.preventDefault();
      this.close({ remember: true });
      return;
    }

    if (event.key !== 'Tab') return;

    // Keep focus inside the dialog while it is modal.
    const focusable = Array.from(
      this.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
    ).filter((el) => !el.disabled && el.offsetParent !== null);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
}

customElements.define('subscribe-popup', SubscribePopup);
